import { Dispatch, MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { subScribeToEvent, unSubScribeToEvent } from 'utils/ClientEventUtils';
import { keepContextAlive } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import {
  ContextChangeAction,
  contextProviderNodeReducer,
  getNodeUpdateAction,
} from 'components/property-editors/contextProviderNodeReducer';
import {
  ContextProviderNode,
  isContextProviderNodeChangedNotification,
  isRefreshPropertyGridEvent,
} from '@decisions/server/DecisionsFramework/Design/Properties';

export interface IUseKeepContextAlive {
  editingContext: string;
  snapshot?: ContextProviderNode;
  prevEditingContext: MutableRefObject<string | undefined>;
  getSubscriberId?: () => string;
  // handleUpdateEvent: (event: any) => void;
  debouncedUpdate: Dispatch<ContextChangeAction> & { clear(): void } & { flush(): void };
  refresh: () => void;
  skipValidationResetOnEachUpdate?: boolean;
  suspended?: boolean;
}

/** server will dispose context shortly after 2 minutes */
export const KEEP_CONTEXT_ALIVE_INTERVAL = 120000;

/**
 * Hook to manage subscribing to changes in a context provider node,
 * as well as keep that editing context alive.
 * @param props
 */
export function useSubscribeToEditingContext({
  debouncedUpdate,
  editingContext,
  getSubscriberId,
  // handleUpdateEvent,
  prevEditingContext,
  refresh,
  snapshot,
  skipValidationResetOnEachUpdate,
}: IUseKeepContextAlive) {
  const keepAliveInterval = useRef(-1);
  /** used to build up new snapshot during rapid event updates, etc. */
  const suspendedRef = useRef(false);
  const snapshotRef = useRef<ContextProviderNode>();
  const shouldRefreshRef = useRef(false);
  // snapshot ref every render, so we have the latest when we need to reference it.
  // partly because snapshot variable gets dereferenced in handleUpdateEvent
  snapshotRef.current = snapshot;
  const handleUpdateEvent = (event: any) => {
    const eventData = event.detail;
    if (isRefreshPropertyGridEvent(eventData)) {
      // not much data here, just being told to refresh the thing.
      if (suspendedRef.current) {
        // if we are in an updates-suspended state, we just cache the fact that we will need to refresh when resumed:
        shouldRefreshRef.current = true;
      } else {
        refresh();
      }
    } else if (isContextProviderNodeChangedNotification(eventData)) {
      let action = getNodeUpdateAction(eventData, skipValidationResetOnEachUpdate);
      if (!action) {
        // something wasn't right, ignore.
        return;
      }
      // take this action and call the reducer directly to update the snapshot
      // store this updated snapshot in a ref for now, so we don't re-render the whole p-grid for each event in a batch of update events
      snapshotRef.current = contextProviderNodeReducer(snapshotRef.current || snapshot, action);
      // call dispatch with the fully updated snapshot, but _debounced_ so we don't try to re-render more than 60fps
      debouncedUpdate.clear(); // don't need the last update
      // queue the update/re-render/dependent firing
      // unless it's suspended, then we just keep the updated snapshot for later.
      suspendedRef.current !== true && debouncedUpdate({ value: snapshotRef.current });
    }
  };

  // Keep update event subscriptions up to date:
  useEffect(() => {
    const id = getSubscriberId?.() || '';
    prevEditingContext.current && unSubScribeToEvent(prevEditingContext.current, id, handleUpdateEvent);
    subScribeToEvent(editingContext, id, handleUpdateEvent);

    // setup keep alive interval
    window.clearTimeout(keepAliveInterval.current);

    const keepAlive = async () => {
      await keepContextAlive({ contextProviderRegistrationId: editingContext });
      // when this has _succeeded_ start the next one:
      keepAliveInterval.current = window.setTimeout(keepAlive, KEEP_CONTEXT_ALIVE_INTERVAL);
    };

    // kick it off via timeout, so we have a timeout to cancel if this effect runs more than once quickly
    keepAliveInterval.current = window.setTimeout(keepAlive, KEEP_CONTEXT_ALIVE_INTERVAL);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContext]);

  // unsubscribe from events when id changes and when un-mounting:
  useEffect(() => {
    return () => {
      const subscriberId = getSubscriberId?.() || '';
      unSubScribeToEvent(editingContext, subscriberId, handleUpdateEvent);
      // cleanup keep alive
      window.clearTimeout(keepAliveInterval.current);
      // clear any debounced updates that could be waiting
      debouncedUpdate.clear();
    };
    // We are attaching the event using editing context id so we need to add the editingContext in the deps.
    // this effect should have no deps, so it can really do its job of cleaning up only on un-mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContext]);

  const suspend = useCallback(() => {
    // recent changes to sync make mappings changes to the original object can cause race conditions attempting to fetch
    // generators for paths that are in the process of being created. The primary use case is a step property grid that
    // has launched a MappingEditor dialog.
    if (!suspendedRef.current) {
      // suspending updates
    }

    suspendedRef.current = true;
  }, []);

  const resume = useCallback(async () => {
    if (suspendedRef.current) {
      if (shouldRefreshRef.current) {
        // we need to refresh, so we don't need any other individual outstanding changes
        return refresh();
      } else {
        debouncedUpdate({ value: snapshotRef.current }); // finally pass snapshot updated in background
        debouncedUpdate.flush(); // flush to trigger immediate update.
      }
    }

    suspendedRef.current = false;
  }, [debouncedUpdate, refresh]);

  return { suspend, resume };
}
